import {
  Chapter,
  CheckTaskAnswerResult,
  CourseWithChapters,
  LessonWithLinks,
  Link,
  Task,
  TaskAnswer,
  LessonTask,
} from "@jelly/lesson";
import { useMutation, useQuery } from "react-query";

import { courseProvider, lessonProvider, progressProvider, taskProvider } from "../providers/local";
import { getChapters, getCourseLessonsProgress } from "../services/Utils";
import { CourseLessonProgress } from "../interfaces/CourseLessonProgress";

interface UseCourseResult {
  course?: CourseWithChapters;
  lesson?: LessonWithLinks;
  relatedLessons?: Link[];
  chapters?: Chapter[];
  tasks?: LessonTask[];
  courseLessonsProgress: CourseLessonProgress[];
  onCheckTaskAnswer: (task: Task, answer: TaskAnswer) => Promise<CheckTaskAnswerResult<TaskAnswer>>;
  onLessonComplete: (lessonId: string) => Promise<void>;
  onTaskComplete: (taskId: string, isCorrect: boolean) => Promise<void>;
}

function getRelatedLessons(lesson?: LessonWithLinks): Link[] {
  if (!lesson) {
    return [];
  }

  return lesson.links.map((lessonLink) => {
    return {
      url: `/course/${lessonLink.course.id}/${lessonLink.lesson.id}`,
      name: lessonLink.lesson.name,
      number: `${lessonLink.course.publicId}.${lessonLink.lesson.publicId}`,
    };
  });
}

export default function useCourseController(courseId: string, lessonId?: string): UseCourseResult {
  const queryCourse = useQuery(["queryCourse", courseId], () => courseProvider.get(courseId), {
    staleTime: 10 * 60 * 1000,
    keepPreviousData: true,
  });

  const queryLessonId = lessonId || queryCourse.data?.chapters[0].chapterLessons[0].lesson.id;

  const queryLesson = useQuery(
    ["queryLesson", courseId, queryLessonId],
    () => {
      // Here `enabled` option, take care that this function never be called if queryLessonId is not string type
      // so we can casting to string
      return lessonProvider.getWithLinks(courseId, queryLessonId as string);
    },
    {
      staleTime: 10 * 60 * 1000,
      keepPreviousData: true,
      enabled: queryLessonId !== undefined,
    }
  );

  const queryTasks = useQuery(
    ["tasks", courseId, queryLessonId],
    () => {
      // Here `enabled` option, take care that this function never be called if queryLessonId is not string type
      // so we can casting to string
      return progressProvider.getLessonTask(queryLessonId as string);
    },
    {
      staleTime: 10 * 60 * 1000,
      enabled: queryLessonId !== undefined,
    }
  );

  const queryLessonResults = useQuery(
    ["lessonResults", courseId],
    () => {
      return progressProvider.getCourseLessonResults(courseId);
    },
    {
      staleTime: 10 * 60 * 1000,
    }
  );

  const queryCourseProgress = useQuery(
    ["queryCoursesProgress", courseId],
    () => progressProvider.getCourseProgress(courseId),
    {
      staleTime: 10 * 60 * 1000,
      keepPreviousData: true,
    }
  );

  const course = queryCourse.data;
  const lesson = queryLesson.data;
  const tasks = queryTasks.data;
  const chapters = getChapters(course, queryLessonResults.data);
  const relatedLessons = getRelatedLessons(lesson);
  const courseLessonsProgress = getCourseLessonsProgress(course, queryLessonResults.data, queryCourseProgress.data);

  const checkTaskAnswerMutation = useMutation(({ task, answer }: { task: Task; answer: TaskAnswer }) => {
    return taskProvider.checkAnswer(task.id, answer);
  });
  const lessonCompleteMutation = useMutation((lessonId: string) => {
    const result = progressProvider.setLessonProgress(lessonId, { isComplete: true });

    // Refetch data about lesson progress
    queryLessonResults.refetch();

    return result;
  });
  const taskCompleteMutation = useMutation(({ taskId, isCorrect }: { taskId: string; isCorrect: boolean }) => {
    const result = progressProvider.setTaskProgress(taskId, { isComplete: true, isCorrect });

    // Refetch progress data for tasks & courses
    queryTasks.refetch();
    queryCourseProgress.refetch();

    return result;
  });

  async function onCheckTaskAnswer(task: Task, answer: TaskAnswer): Promise<CheckTaskAnswerResult<TaskAnswer>> {
    return await checkTaskAnswerMutation.mutateAsync({ task, answer });
  }

  async function onLessonComplete(lessonId: string): Promise<void> {
    return await lessonCompleteMutation.mutateAsync(lessonId);
  }

  async function onTaskComplete(taskId: string, isCorrect: boolean): Promise<void> {
    return await taskCompleteMutation.mutateAsync({ taskId, isCorrect });
  }

  return {
    course,
    lesson,
    relatedLessons,
    chapters,
    tasks,
    courseLessonsProgress,
    onCheckTaskAnswer,
    onLessonComplete,
    onTaskComplete,
  };
}
